import {
    PortalOptionPositionRow,
    PortalOptionPositionTableProps
} from '@/components/portal-modules/PortalOptionPositionTable';
import { defaultColumn } from '@/config/config';
import { convertToPortalOptionPositionRow, portalOptionPositionColumns } from '@/helpers/portalOptionPositionHelper';
import { useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import {
    ColumnDef,
    ExpandedState,
    filterFns,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useDateFormatter, useLocale } from 'react-aria';
import { useSso } from '../providers/SSOProvider';
import { useDebouncedValue } from './useDebouncedValue';
import { fuzzyGlobalFilter } from './useOpenPositionTable';

interface UsePortalOptionPositionTableProps extends PortalOptionPositionTableProps {}

const usePortalOptionPositionTable = (props: UsePortalOptionPositionTableProps) => {
    const { summary, baseCurrency } = props;

    const { locale } = useLocale();
    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);

    const dateFormatter = useDateFormatter({ timeZone: 'UTC' });

    const { currenciesApi } = useSso();
    const { data: cashEntryCurrencies } = currenciesApi;

    const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [hydrated, setHydrated] = useState(false);

    const totalCurrentValueInBaseCurrency = useMemo(() => {
        return (
            summary?.fxOptionSummary?.data.reduce((acc, row) => {
                return acc + Number(row.currentValueInBaseCurrency || 0);
            }, 0) || 0
        );
    }, [summary?.fxOptionSummary?.data]);

    const columns: ColumnDef<PortalOptionPositionRow>[] = useMemo(
        () =>
            portalOptionPositionColumns(baseCurrency, accountType, totalCurrentValueInBaseCurrency, instrumentConfigs),
        [baseCurrency, instrumentConfigs, accountType, totalCurrentValueInBaseCurrency]
    );

    const formattedPortalPositions = useMemo<PortalOptionPositionRow[]>(() => {
        const optionPositions = summary?.fxOptionSummary?.data || [];
        return optionPositions.map((position) =>
            convertToPortalOptionPositionRow(locale, position, dateFormatter, cashEntryCurrencies)
        );
    }, [locale, summary, dateFormatter, cashEntryCurrencies]);

    const tableInstance = useReactTable({
        data: formattedPortalPositions,
        columns,
        state: { globalFilter, columnSizing, sorting },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => fuzzyGlobalFilter(row, columnId, filterValue, dateFormatter),
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        onExpandedChange: setExpandedRows,
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        getExpandedRowModel: getExpandedRowModel(),
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    useEffect(() => {
        if (summary && !hydrated) setHydrated(true);
    }, [summary, hydrated]);

    return { debouncedTableInstance, columnSizing, sorting, formattedPortalPositions, expandedRows, globalFilter };
};

export default usePortalOptionPositionTable;
